import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MoreHorizontal, Settings } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import DarkModeToggle from "../../components/DarkModeToggle";
import { useApp } from "../../context/AppContext";
import { useBuildReportAsync } from "../../hooks/useBuildReportAsync";
import { useReportHistory } from "../../hooks/useReportHistory";
import { useTrackPageView } from "../../hooks/useTrackPageView";
import { trackReportCreation } from "../../utils/mixpanel";
import { DEFAULT_PERSONA } from "../../utils/personas";
import { samplePrompts } from "../../utils/samplePrompts";
import SpateLogo from "../../components/SpateLogo";

function Landing() {
  useTrackPageView("Landing");
  const navigate = useNavigate();
  const buildReport = useBuildReportAsync();
  const { addRecentSearch, preferences } = useApp();
  const { data: reportHistory = [] } = useReportHistory();
  const [showAllPrompts, setShowAllPrompts] = useState(false);

  // Show sample prompts if user has no reports OR if the setting is enabled
  const showSamplePrompts = reportHistory.length === 0 || preferences.showSamplePrompts;

  // Filter prompts based on whether we're showing all
  const visiblePrompts = showAllPrompts ? samplePrompts : samplePrompts.filter((p) => p.primary);

  const handlePromptClick = async (prompt, persona) => {
    try {
      const result = await buildReport.mutateAsync({
        request: prompt,
        persona,
      });
      trackReportCreation({
        report_id: result.id,
        request: prompt,
        persona: persona,
        source: "sample_prompt",
      });
      addRecentSearch(prompt);
      navigate(`/v1/report/${result.id}`);
    } catch (error) {
      console.error("Failed to create plan:", error);
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const request = formData.get("request");
    const persona = formData.get("persona") || DEFAULT_PERSONA.value;
    try {
      const result = await buildReport.mutateAsync({ request, persona });
      trackReportCreation({
        report_id: result.id,
        request: request,
        persona: persona,
        source: "user_input",
      });
      addRecentSearch(request);
      navigate(`/v1/report/${result.id}`);
    } catch (error) {
      console.error("Failed to create plan:", error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Settings and Dark mode toggle - top left */}
      <div className="absolute top-0 left-0 p-6 flex items-center gap-2">
        <Link to="/v1/settings">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
          >
            <Settings className="h-4 w-4" />
            <span className="sr-only">Settings</span>
          </Button>
        </Link>
        <div className="h-5 w-px bg-border" />
        <DarkModeToggle />
      </div>

      {/* Navigation - top right */}
      <nav className="absolute top-0 right-0 p-6">
        <Button variant="outline" asChild>
          <Link to="/v1/reports">View Reports</Link>
        </Button>
      </nav>

      {/* Main Content */}
      <div className="flex min-h-screen items-center justify-center p-6">
        <Card className="w-full max-w-2xl shadow-xl">
          <CardHeader className="space-y-2 text-center">
            <SpateLogo className="mx-auto h-6 w-auto" />
            <CardTitle className="text-2xl tracking-tight">
              Report Builder
              <span className="ml-2 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary border border-primary/20">
                Beta
              </span>
            </CardTitle>
            <CardDescription className="text-lg">
              Ask questions about beauty trends, consumer behavior, and market insights
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="request" className="text-base font-medium">
                    What do you want to know?
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    {navigator.platform.includes("Mac") ? "Cmd" : "Ctrl"}+Enter for newline
                  </span>
                </div>
                <Textarea
                  id="request"
                  name="request"
                  placeholder="e.g., What's trending in skincare for Gen Z? How is the fragrance market performing year-over-year?"
                  className="min-h-[120px] resize-none text-base"
                  required
                  disabled={buildReport.isLoading}
                  autoFocus
                  onKeyDown={(e) => {
                    // Submit on Enter (without modifier keys)
                    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                      e.preventDefault();
                      e.currentTarget.form.requestSubmit();
                    }
                    // Allow Cmd/Ctrl+Enter to create a newline
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      const textarea = e.currentTarget;
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const value = textarea.value;
                      textarea.value = value.substring(0, start) + "\n" + value.substring(end);
                      textarea.selectionStart = textarea.selectionEnd = start + 1;
                    }
                  }}
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full text-base"
                disabled={buildReport.isLoading}
              >
                {buildReport.isLoading ? (
                  <>
                    <span className="mr-2">Creating Report Plan</span>
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  </>
                ) : (
                  "Generate Report"
                )}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or try one of these</span>
              </div>
            </div>

            {/* Sample Prompts - only show for first-time users */}
            {showSamplePrompts && (
              <>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {visiblePrompts.map((prompt, index) => {
                      const Icon = prompt.icon;
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handlePromptClick(prompt.fullPrompt, prompt.testId)}
                          disabled={buildReport.isLoading}
                          className="group relative inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm transition-all hover:bg-accent hover:border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
                          <span className="font-medium">{prompt.label}</span>
                        </button>
                      );
                    })}
                    {/* Show more/less button */}
                    <button
                      type="button"
                      onClick={() => setShowAllPrompts(!showAllPrompts)}
                      disabled={buildReport.isLoading}
                      className="group relative inline-flex items-center gap-2 rounded-full border border-dashed border-border bg-card px-4 py-2 text-sm transition-all hover:bg-accent hover:border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={showAllPrompts ? "Show less options" : "Show more options"}
                    >
                      <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
                      <span className="font-medium">{showAllPrompts ? "Less" : "More"}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Landing;
